import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ActivityFeedRow } from '../schemas'

type MatterActivityEntry = {
  rows: ActivityFeedRow[]
  /* True when the matter has activity past these rows, which the feed never fetches. */
  hasMore: boolean
  /* True once a fetch has completed for this matter, so an empty feed reads as empty rather than pending. */
  initialLoadComplete: boolean
  /* Epoch ms of the last completed fetch, or kStaleActivityTimestamp once invalidated. */
  lastRefresh: number | null
  isLoading: boolean
  isError: boolean
}

type MatterActivityFeedState = {
  byMatterId: Partial<Record<string, MatterActivityEntry>>
}

const initialState: MatterActivityFeedState = { byMatterId: {} }

/**
 * A `lastRefresh` marking a matter's feed stale: older than any real fetch time, so the cache check
 * treats it as expired. A completed fetch overwrites it. Read via `selectMatterActivityStale`.
 */
export const kStaleActivityTimestamp = 0

function entryFor(state: MatterActivityFeedState, matterId: string): MatterActivityEntry {
  const entry = state.byMatterId[matterId] ?? {
    rows: [],
    hasMore: false,
    initialLoadComplete: false,
    lastRefresh: null,
    isLoading: false,
    isError: false,
  }
  state.byMatterId[matterId] = entry
  return entry
}

export const matterActivityFeedSlice = createSlice({
  name: 'matterActivityFeedState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterActivity: (_state, _action: PayloadAction<{ matterId: string; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },
    // ============== Slice Actions ============== >
    setActivityLoading: (state, action: PayloadAction<{ matterId: string; loading: boolean }>) => {
      entryFor(state, action.payload.matterId).isLoading = action.payload.loading
    },
    setActivityError: (state, action: PayloadAction<{ matterId: string; error: boolean }>) => {
      entryFor(state, action.payload.matterId).isError = action.payload.error
    },
    setActivityRows: (state, action: PayloadAction<{ matterId: string; rows: ActivityFeedRow[]; hasMore: boolean }>) => {
      const entry = entryFor(state, action.payload.matterId)
      entry.rows = action.payload.rows
      entry.hasMore = action.payload.hasMore
      entry.initialLoadComplete = true
    },
    setActivityLastRefresh: (state, action: PayloadAction<{ matterId: string; timestamp: number }>) => {
      entryFor(state, action.payload.matterId).lastRefresh = action.payload.timestamp
    },
    // Nothing is cached to invalidate for a matter never visited, and its first visit fetches fresh anyway.
    invalidateMatterActivity: (state, action: PayloadAction<{ matterId: string }>) => {
      const entry = state.byMatterId[action.payload.matterId]
      if (entry) entry.lastRefresh = kStaleActivityTimestamp
    },
    nullifyData: () => initialState,
  },
})

export const MatterActivityFeedActions = matterActivityFeedSlice.actions

export default matterActivityFeedSlice.reducer
